/* Ouder-portal statuspagina — publiek via status_token.
   Route: /status/:token
   Toont de actuele status + tijdlijn + eventuele aanbod-link. */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Baby, ArrowRight, Clock, CheckCircle2, XCircle, ClipboardList, Calendar } from "lucide-react";
import { formatApiError } from "@/lib/api";

const api = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: false,
});

const STATUS_LABELS = {
  nieuw: "Nieuw", in_beoordeling: "In beoordeling",
  beschikbaarheid_check: "Beschikbaarheid wordt gecheckt",
  wachtlijst: "Op wachtlijst",
  aanbod_verstuurd: "Aanbod verstuurd", aanbod_geaccepteerd: "Aanbod geaccepteerd",
  aanbod_afgewezen: "Aanbod afgewezen", aanbod_verlopen: "Aanbod verlopen",
  contract_verstuurd: "Contract verstuurd", contract_getekend: "Contract getekend",
  actieve_plaatsing: "Actieve plaatsing", afgewezen: "Afgewezen", geannuleerd: "Geannuleerd",
};

// Stappen voor voortgangsindicator
const STEPS = [
  { key: "aanmelding", label: "Aanmelding", statuses: ["nieuw", "in_beoordeling"] },
  { key: "beoordeling", label: "Beoordeling", statuses: ["beschikbaarheid_check"] },
  { key: "aanbod", label: "Aanbod", statuses: ["aanbod_verstuurd", "aanbod_geaccepteerd", "aanbod_afgewezen", "aanbod_verlopen"] },
  { key: "contract", label: "Contract", statuses: ["contract_verstuurd", "contract_getekend"] },
  { key: "plaatsing", label: "Plaatsing", statuses: ["actieve_plaatsing"] },
];

function currentStepIdx(status) {
  for (let i = 0; i < STEPS.length; i++) {
    if (STEPS[i].statuses.includes(status)) return i;
  }
  if (status === "wachtlijst") return 1;
  if (status === "afgewezen" || status === "geannuleerd") return -1;
  return 0;
}

export default function OuderStatusPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = () => {
    api.get(`/public/aanmelding/status/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(formatApiError(e.response?.data?.detail) || "Status niet gevonden"));
  };
  useEffect(() => { load(); }, [token]);

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
        <Card className="max-w-md p-8 text-center bg-white">
          <XCircle className="h-10 w-10 mx-auto mb-2 text-neutral-400" />
          <div className="text-xl font-display mb-2">Status niet gevonden</div>
          <div className="text-sm text-neutral-600">{err}</div>
          <div className="text-xs text-neutral-500 mt-4">Controleer of u de volledige link uit de bevestigingsmail heeft gebruikt.</div>
        </Card>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Laden…</div>;

  const primair = data.kleur_primair || "#2D4A3E";
  const stepIdx = currentStepIdx(data.status);
  const isDone = data.status === "actieve_plaatsing";
  const isRejected = ["afgewezen", "aanbod_afgewezen", "geannuleerd"].includes(data.status);
  const hasAanbod = data.status === "aanbod_verstuurd" && data.aanbod_token;

  return (
    <div className="min-h-screen p-6" style={{ background: `linear-gradient(135deg, ${primair}12, ${primair}05)` }} data-testid="status-page">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          {data.organisation_logo ? (
            <img src={data.organisation_logo.startsWith("http") ? data.organisation_logo : `${process.env.REACT_APP_BACKEND_URL}${data.organisation_logo}`}
                 alt="logo" className="w-14 h-14 rounded-xl object-cover" />
          ) : (
            <div className="w-14 h-14 rounded-xl flex items-center justify-center" style={{ background: primair }}>
              <Baby className="h-7 w-7 text-white" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-display leading-tight">{data.organisation_name}</h1>
            <div className="text-xs text-neutral-500">Status van uw aanmelding</div>
          </div>
        </div>

        {/* Info-kaart */}
        <Card className="p-5 bg-white mb-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Kind</div>
              <div className="text-lg font-display" data-testid="status-kind-naam">{data.kind_voornaam} {data.kind_achternaam}</div>
              <div className="text-xs text-neutral-500 mt-0.5">Referentie <span className="font-mono">{data.referentie}</span></div>
            </div>
            <Badge variant="outline" className="border-neutral-300 text-neutral-700 bg-white" data-testid="status-badge">
              {STATUS_LABELS[data.status] || data.status}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Locatie</div>
              <div className="font-medium">{data.location_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">Gewenste start</div>
              <div className="font-medium">{data.gewenste_startdatum || "—"}</div>
            </div>
          </div>
          {data.rondleiding && (
            <div className="mt-3 p-3 rounded-md text-xs" style={{ background: `${primair}0d` }}>
              <div className="flex items-center gap-1.5 font-medium mb-1"><Calendar className="h-3.5 w-3.5" style={{ color: primair }} /> Rondleiding</div>
              {data.rondleiding.datum
                ? <div>{data.rondleiding.datum} om {data.rondleiding.tijd_van || ""}</div>
                : <div>Voorkeur: {data.rondleiding.voorkeur_datum} {data.rondleiding.voorkeur_tijd || ""}</div>}
              {data.rondleiding.opmerking && <div className="mt-1 text-neutral-600 italic">{data.rondleiding.opmerking}</div>}
            </div>
          )}
        </Card>

        {/* Voortgangsindicator */}
        {!isRejected && (
          <Card className="p-5 bg-white mb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-sm font-medium">Voortgang</div>
              <div className="text-xs text-neutral-500">Stap {Math.max(1, stepIdx + 1)} van {STEPS.length}</div>
            </div>
            <div className="relative">
              <div className="absolute top-3 left-3 right-3 h-0.5 bg-neutral-200" />
              <div className="absolute top-3 left-3 h-0.5 transition-all"
                   style={{
                     width: stepIdx > 0 ? `calc(${(stepIdx / (STEPS.length - 1)) * 100}% - 12px)` : "0%",
                     background: primair,
                   }} />
              <div className="relative flex justify-between">
                {STEPS.map((s, idx) => {
                  const done = idx <= stepIdx;
                  const isCurrent = idx === stepIdx;
                  return (
                    <div key={s.key} className="flex flex-col items-center" style={{ width: `${100 / STEPS.length}%` }}>
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold z-10 border-2 ${
                        done ? "text-white" : "bg-white border-neutral-300 text-neutral-400"
                      }`} style={done ? { background: primair, borderColor: primair } : {}}>
                        {done ? (isCurrent && !isDone ? idx + 1 : "✓") : idx + 1}
                      </div>
                      <div className={`text-[10px] mt-1.5 text-center ${done ? "text-neutral-900 font-medium" : "text-neutral-400"}`}>
                        {s.label}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        )}

        {/* Aanbod-CTA */}
        {hasAanbod && (
          <Card className="p-5 mb-4 border-2" style={{ borderColor: primair, background: `${primair}0d` }} data-testid="status-aanbod-cta">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="font-display text-lg mb-1">U heeft een aanbod!</div>
                <div className="text-sm text-neutral-700">
                  Plek in <strong>{data.aanbod_group_name}</strong>.
                  {data.aanbod_valid_until && <> Geldig tot {String(data.aanbod_valid_until).slice(0, 10)}.</>}
                </div>
              </div>
              <Link to={`/aanbod/${data.aanbod_token}`}>
                <Button style={{ background: primair }} className="text-white" data-testid="btn-bekijk-aanbod">
                  Bekijk aanbod <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </Link>
            </div>
          </Card>
        )}

        {/* Actieve plaatsing */}
        {isDone && (
          <Card className="p-5 mb-4 bg-emerald-50 border-emerald-200" data-testid="status-active">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-8 w-8 text-emerald-700 shrink-0" />
              <div>
                <div className="font-display text-lg text-emerald-900">Welkom bij {data.organisation_name}!</div>
                <div className="text-sm text-emerald-800">Uw kind is toegewezen aan een groep. U ontvangt een activatie-e-mail voor het ouder-portaal.</div>
              </div>
            </div>
          </Card>
        )}

        {/* Afwijzing */}
        {isRejected && (
          <Card className="p-5 mb-4 bg-red-50 border-red-200" data-testid="status-rejected">
            <div className="flex items-center gap-3">
              <XCircle className="h-8 w-8 text-red-700 shrink-0" />
              <div>
                <div className="font-display text-lg text-red-900">
                  {data.status === "afgewezen" ? "Aanmelding afgewezen" :
                   data.status === "aanbod_afgewezen" ? "U heeft het aanbod afgewezen" : "Aanmelding geannuleerd"}
                </div>
                {data.afwijzingsreden && <div className="text-sm text-red-800 mt-1"><em>{data.afwijzingsreden}</em></div>}
              </div>
            </div>
          </Card>
        )}

        {/* Wachtlijst */}
        {data.status === "wachtlijst" && (
          <Card className="p-5 mb-4 bg-amber-50 border-amber-200">
            <div className="flex items-center gap-3">
              <ClipboardList className="h-8 w-8 text-amber-700 shrink-0" />
              <div>
                <div className="font-display text-lg text-amber-900">Op wachtlijst geplaatst</div>
                <div className="text-sm text-amber-800">Zodra er plek beschikbaar komt, ontvangt u een aanbod per e-mail.</div>
              </div>
            </div>
          </Card>
        )}

        {/* Tijdlijn */}
        <Card className="p-5 bg-white">
          <div className="text-sm font-medium mb-3">Tijdlijn</div>
          <div className="space-y-2">
            {(data.timeline || []).slice().reverse().map((t, i) => (
              <div key={i} className="flex items-start gap-3 text-xs pb-2 border-b border-neutral-100 last:border-0" data-testid={`timeline-${i}`}>
                <Clock className="h-3.5 w-3.5 mt-0.5 text-neutral-400 shrink-0" />
                <div className="flex-1">
                  <div className="font-medium text-neutral-800">{STATUS_LABELS[t.status] || t.status}</div>
                  <div className="text-neutral-500">{new Date(t.timestamp).toLocaleString('nl-NL')}</div>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="text-center text-[10px] text-neutral-400 mt-6">
          {data.organisation_name} · Powered by <strong>Webster</strong>
        </div>
      </div>
    </div>
  );
}
